/* global axios */
import { ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { downloadFile as ordinaryDownload } from '@chatwoot/utils';
import { privateMediaPath, isTransientMediaError, fetchMediaWithRetry } from 'dashboard/helpers/authenticatedMedia.mjs';
import { acquireMedia, invalidateMedia, sameOriginMedia } from 'dashboard/helpers/mediaResource';

export function usePrivateMedia(sourceGetter) {
  const mediaUrl = ref(''), mediaError = ref(false), mediaLoading = ref(false);
  let lease, generation = 0, source = '', alive = true, elementRetries = 0, retryable = false;
  let decodeRetries = 0, retryTimer;

  async function load(force = false) {
    const version = ++generation;
    const previous = lease;
    lease = null;
    mediaError.value = false;
    mediaUrl.value = '';
    retryable = false;
    if (!source) { mediaLoading.value = false; previous?.release(); return; }
    if (!privateMediaPath(source, window.location.origin) && (!force || !sameOriginMedia(source))) {
      previous?.release();
      mediaLoading.value = false;
      // Removing src for a tick also lets external image elements retry without
      // changing signed query parameters or sending authentication cross-origin.
      await nextTick();
      if (alive && version === generation) mediaUrl.value = source;
      return;
    }
    mediaLoading.value = true;
    lease = acquireMedia(source);
    const current = lease;
    try {
      const url = await current.promise;
      if (alive && version === generation) mediaUrl.value = url;
    } catch (error) {
      if (alive && version === generation) {
        mediaError.value = true;
        retryable = isTransientMediaError(error);
      }
    } finally {
      previous?.release();
      if (alive && version === generation) mediaLoading.value = false;
    }
  }

  const retryMedia = () => {
    if (!alive || mediaLoading.value) return;
    clearTimeout(retryTimer);
    if (mediaUrl.value.startsWith('blob:')) invalidateMedia(source);
    elementRetries = 0;
    return load(true);
  };
  const mediaFailed = () => {
    mediaError.value = true;
    // A successful fetch can still fail in the media element. Reacquire once
    // instead of keeping a failed object URL until the whole page is refreshed.
    if (!mediaLoading.value && mediaUrl.value.startsWith('blob:') && decodeRetries++ === 0) {
      const version = generation;
      retryTimer = setTimeout(() => {
        if (alive && version === generation) retryMedia();
      }, 750);
      return;
    }
    // Direct normal URLs have no HTTP status available to the DOM. A single
    // authenticated fetch classifies the failure; denied/missing files stop here.
    if (!mediaLoading.value && elementRetries++ === 0 && !mediaUrl.value.startsWith('blob:')) {
      return load(true);
    }
    retryable = false;
  };
  const mediaLoaded = () => { mediaError.value = false; };
  const online = () => {
    if (mediaError.value && retryable) retryMedia();
  };
  const visible = () => { if (document.visibilityState === 'visible') online(); };
  window.addEventListener('online', online);
  document.addEventListener('visibilitychange', visible);
  watch(sourceGetter, value => {
    clearTimeout(retryTimer);
    source = value || '';
    elementRetries = 0;
    decodeRetries = 0;
    load();
  }, { immediate: true });
  onBeforeUnmount(() => {
    alive = false; generation++;
    clearTimeout(retryTimer);
    window.removeEventListener('online', online);
    document.removeEventListener('visibilitychange', visible);
    lease?.release();
  });
  return { mediaUrl, mediaError, mediaLoading, retryMedia, mediaFailed, mediaLoaded };
}

export async function downloadFile(options) {
  if (!privateMediaPath(options.url, window.location.origin)) return ordinaryDownload(options);
  const result = await fetchMediaWithRetry(
    url => axios.get(url, { responseType: 'blob', timeout: 20000 }), options.url
  );
  const disposition = result.headers['content-disposition'] || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = disposition.match(/filename="([^"]+)"/i);
  let filename = options.filename || plain?.[1] || `arquivo${options.extension ? `.${options.extension}` : ''}`;
  if (encoded) { try { filename = decodeURIComponent(encoded[1]); } catch { /* keep safe fallback */ } }
  const url = URL.createObjectURL(result.data);
  const link = document.createElement('a');
  link.href = url; link.download = filename; link.rel = 'noopener';
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
